import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Clock, RefreshCw, CheckCircle, XCircle, AlertTriangle, Calendar,
  Building2, Search, Plus, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Card } from "../../../components/ui/Card";
import { Button } from "../../../components/ui/Button";
import { Modal } from "../../../components/ui/Modal";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { EmptyState } from "../../../components/ui/EmptyState";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { useBizAttendance, useBizUpsertAttendance } from "../../../hooks/useBizAdmin";
import { useAuth } from "../../../hooks/useAuth";
import type { AttendanceRecord, AttendanceStatus } from "../../../lib/attendanceApi";
import { bizApi } from "../../../lib/bizApi";
import { UserPicker } from "../../../components/ui/UserPicker";
import type { SelectedUser } from "../../../components/ui/UserPicker";

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<AttendanceStatus, { label: string; bg: string; text: string; color: string }> = {
  PRESENT:  { label: "Present",  bg: "bg-[#f0fdf4]", text: "text-[#16a34a]", color: "#16a34a" },
  LATE:     { label: "Late",     bg: "bg-[#fffbeb]", text: "text-[#d97706]", color: "#d97706" },
  ABSENT:   { label: "Absent",   bg: "bg-[#fef2f2]", text: "text-[#dc2626]", color: "#dc2626" },
  ON_LEAVE: { label: "On Leave", bg: "bg-[#eff3ff]", text: "text-[#2447d9]", color: "#2447d9" },
};

const STATUS_ORDER: AttendanceStatus[] = ["PRESENT", "LATE", "ON_LEAVE", "ABSENT"];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function shiftDate(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatDisplayDate(d: string) {
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const dt = new Date(d + "T00:00:00Z");
  return `${DAYS[dt.getUTCDay()]}, ${MONTHS[dt.getUTCMonth()]} ${dt.getUTCDate()}`;
}


// ── Summary chips ─────────────────────────────────────────────────────────────

function SummaryChips({
  counts,
  activeFilter,
  onFilter,
}: {
  counts: Record<AttendanceStatus, number>;
  activeFilter: AttendanceStatus | "ALL";
  onFilter: (s: AttendanceStatus | "ALL") => void;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {STATUS_ORDER.map((s) => {
        const cfg = STATUS_CONFIG[s];
        const active = activeFilter === s;
        return (
          <button
            key={s}
            onClick={() => onFilter(active ? "ALL" : s)}
            className={`rounded-xl px-3 py-2.5 text-center border-2 transition-all ${
              active
                ? `border-current ${cfg.bg} ${cfg.text}`
                : `${cfg.bg} border-transparent hover:border-current/30`
            }`}
          >
            <p className={`text-xl font-bold ${cfg.text}`}>{counts[s]}</p>
            <p className="text-[11px] font-medium text-[#64748b] mt-0.5">{cfg.label}</p>
          </button>
        );
      })}
    </div>
  );
}

// ── Records table ─────────────────────────────────────────────────────────────

function RecordsTable({
  records,
  onEdit,
}: {
  records: (AttendanceRecord & { date?: string })[];
  showDate?: boolean;
  onEdit?: (userId: string) => void;
}) {
  const showDate = records.some((r) => r.date);

  if (records.length === 0) {
    return (
      <EmptyState
        icon={<Clock className="h-7 w-7 text-[#94a3b8]" />}
        title="No attendance records"
        description="No records match the current filters."
        className="py-10"
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
            {showDate && <th className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide">Date</th>}
            <th className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide">Employee</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide">Status</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">Time In</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">Time Out</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide">Notes</th>
            {onEdit && <th className="px-5 py-3" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f1f5f9]">
          {records.map((r, i) => {
            const cfg = STATUS_CONFIG[r.status];
            return (
              <tr key={`${r.date ?? ""}-${r.userId}-${i}`} className="bg-white hover:bg-[#f8fafc] transition-colors">
                {showDate && (
                  <td className="px-5 py-3 whitespace-nowrap">
                    <p className="text-xs font-semibold text-[#0f172a]">{formatDisplayDate(r.date!)}</p>
                    <p className="text-[10px] text-[#94a3b8]">{r.date}</p>
                  </td>
                )}
                <td className="px-5 py-3">
                  <p className="font-medium text-[#0f172a]">{r.employeeName ?? r.userId}</p>
                  {r.employeeName && <p className="text-xs text-[#94a3b8]">{r.userId}</p>}
                </td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                    {cfg.label}
                  </span>
                </td>
                <td className="px-5 py-3 text-[#64748b]">{r.timeIn ?? "—"}</td>
                <td className="px-5 py-3 text-[#64748b]">{r.timeOut ?? "—"}</td>
                <td className="px-5 py-3 text-[#64748b] max-w-[160px] truncate">{r.notes ?? "—"}</td>
                {onEdit && (
                  <td className="px-5 py-3">
                    <button
                      onClick={() => onEdit(r.userId)}
                      className="rounded-lg border border-[#e2e8f0] px-3 py-1 text-xs font-medium text-[#374151] hover:bg-[#f1f5f9] transition-colors"
                    >
                      Edit
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Edit Attendance Modal ─────────────────────────────────────────────────────

function EditAttendanceModal({ record, date, onClose, onSaved }: {
  record: AttendanceRecord; date: string; onClose: () => void; onSaved: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const { activeBusinessId } = useAuth();
  const upsert = useBizUpsertAttendance(activeBusinessId ?? "");
  const [status, setStatus] = useState<AttendanceStatus>(record.status);
  const [timeIn, setTimeIn]   = useState(record.timeIn ?? "");
  const [timeOut, setTimeOut] = useState(record.timeOut ?? "");
  const [notes, setNotes]     = useState(record.notes ?? "");

  const handleSave = async () => {
    try {
      await upsert.mutateAsync({ userId: record.userId, employeeName: record.employeeName, date, status, timeIn: timeIn || undefined, timeOut: timeOut || undefined, notes: notes || undefined });
      toast.success("Attendance saved.");
      onSaved();
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to save attendance. Please try again.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3 text-sm">
          <p className="font-semibold text-[#0f172a]">{record.employeeName ?? record.userId}</p>
          <p className="text-xs text-[#64748b]">{record.userId} · {date}</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-2">Status</label>
          <div className="grid grid-cols-2 gap-2">
            {STATUS_ORDER.map((s) => {
              const cfg = STATUS_CONFIG[s];
              return (
                <button key={s} type="button" onClick={() => setStatus(s)}
                  className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-all ${
                    status === s ? `border-current ${cfg.bg} ${cfg.text}` : "border-[#e2e8f0] text-[#64748b] hover:border-[#cbd5e1]"
                  }`}>
                  {s === "PRESENT"  && <CheckCircle className="h-4 w-4" />}
                  {s === "ABSENT"   && <XCircle className="h-4 w-4" />}
                  {s === "LATE"     && <AlertTriangle className="h-4 w-4" />}
                  {s === "ON_LEAVE" && <Calendar className="h-4 w-4" />}
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-1">Time In</label>
            <input type="time" value={timeIn} onChange={(e) => setTimeIn(e.target.value)}
              className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-1">Time Out</label>
            <input type="time" value={timeOut} onChange={(e) => setTimeOut(e.target.value)}
              className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#64748b] mb-1">Notes (optional)</label>
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Doctor's appointment"
            className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
        </div>

        {(record.recordedBy || record.updatedAt) && (
          <div className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 space-y-1">
            <p className="text-xs font-semibold text-[#374151] uppercase tracking-wide">Audit</p>
            <div className="grid grid-cols-2 gap-x-4 text-xs">
              {record.recordedBy && (
                <div><p className="text-[#94a3b8]">Recorded By</p><p className="font-medium text-[#374151]">{record.recordedBy}</p></div>
              )}
              {record.updatedAt && (
                <div><p className="text-[#94a3b8]">Last Updated</p><p className="font-medium text-[#374151]">{new Date(record.updatedAt).toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" })}</p></div>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleSave} disabled={upsert.isPending} fullWidth>
            {upsert.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── New Attendance Entry ──────────────────────────────────────────────────────

function NewAttendanceEntry({ date, onClose, onSaved }: { date: string; onClose: () => void; onSaved: () => void }) {
  const { toasts, toast, dismissToast } = useToast();
  const { activeBusinessId } = useAuth();
  const upsert = useBizUpsertAttendance(activeBusinessId ?? "");
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);
  const [userError, setUserError] = useState("");
  const [entryDate, setEntryDate] = useState(date);
  const [status, setStatus] = useState<AttendanceStatus>("PRESENT");
  const [timeIn, setTimeIn]   = useState("");
  const [timeOut, setTimeOut] = useState("");
  const [notes, setNotes]     = useState("");

  const handleSave = async () => {
    if (!selectedUser) { setUserError("Please select an employee."); return; }
    if (!entryDate) { toast.error("Please select a date."); return; }
    setUserError("");
    try {
      await upsert.mutateAsync({ userId: selectedUser.userId, employeeName: selectedUser.name, date: entryDate, status, timeIn: timeIn || undefined, timeOut: timeOut || undefined, notes: notes || undefined });
      toast.success("Attendance recorded.");
      onSaved();
    } catch {
      toast.error("Failed to record attendance. Please try again.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1.5">Employee <span className="text-[#dc2626]">*</span></label>
          <UserPicker value={selectedUser} onChange={setSelectedUser} businessId={activeBusinessId ?? undefined} />
          {userError && <p className="text-xs text-[#dc2626] mt-1">{userError}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Date</label>
          <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)}
            className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-2">Status</label>
          <div className="grid grid-cols-2 gap-2">
            {STATUS_ORDER.map((s) => {
              const cfg = STATUS_CONFIG[s];
              return (
                <button key={s} type="button" onClick={() => setStatus(s)}
                  className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-all ${
                    status === s ? `border-current ${cfg.bg} ${cfg.text}` : "border-[#e2e8f0] text-[#64748b] hover:border-[#cbd5e1]"
                  }`}>
                  {s === "PRESENT"  && <CheckCircle className="h-4 w-4" />}
                  {s === "ABSENT"   && <XCircle className="h-4 w-4" />}
                  {s === "LATE"     && <AlertTriangle className="h-4 w-4" />}
                  {s === "ON_LEAVE" && <Calendar className="h-4 w-4" />}
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-1">Time In</label>
            <input type="time" value={timeIn} onChange={(e) => setTimeIn(e.target.value)}
              className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-1">Time Out</label>
            <input type="time" value={timeOut} onChange={(e) => setTimeOut(e.target.value)}
              className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#64748b] mb-1">Notes (optional)</label>
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Doctor's appointment"
            className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleSave} disabled={upsert.isPending} fullWidth>
            {upsert.isPending ? "Saving…" : "Mark Attendance"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Date Range View ───────────────────────────────────────────────────────────

function DateRangeView({ bizId }: { bizId: string }) {
  const today = todayIso();
  const defaultStart = shiftDate(today, -6);

  const [rangeStart, setRangeStart] = useState(defaultStart);
  const [rangeEnd, setRangeEnd]     = useState(today);
  const [loadedStart, setLoadedStart] = useState(defaultStart);
  const [loadedEnd, setLoadedEnd]     = useState(today);
  const [statusFilter, setStatusFilter] = useState<AttendanceStatus | "ALL">("ALL");
  const [nameSearch, setNameSearch]     = useState("");

  const { data: allRecords = [], isLoading } = useQuery({
    queryKey: ["biz", bizId, "attendance-range", loadedStart, loadedEnd],
    queryFn: () => bizApi.listAttendanceRange(bizId, loadedStart, loadedEnd).then((r) => r.items),
    enabled: !!bizId && !!loadedStart && !!loadedEnd,
  });

  const filtered = useMemo(() => {
    return allRecords
      .filter((r) => statusFilter === "ALL" || r.status === statusFilter)
      .filter((r) => !nameSearch || (r.employeeName ?? r.userId).toLowerCase().includes(nameSearch.toLowerCase()))
      .sort((a, b) => a.date.localeCompare(b.date) || (a.employeeName ?? "").localeCompare(b.employeeName ?? ""));
  }, [allRecords, statusFilter, nameSearch]);

  const rangeCounts = useMemo(() => {
    const c = { PRESENT: 0, LATE: 0, ABSENT: 0, ON_LEAVE: 0 };
    for (const r of allRecords) c[r.status]++;
    return c;
  }, [allRecords]);

  const exceedsMax = rangeStart && rangeEnd &&
    Math.round((new Date(rangeEnd + "T00:00:00Z").getTime() - new Date(rangeStart + "T00:00:00Z").getTime()) / 86_400_000) >= 31;

  const handleLoad = () => {
    if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) return;
    setLoadedStart(rangeStart);
    setLoadedEnd(rangeEnd);
  };

  return (
    <div className="space-y-4">
      {/* Compact range bar */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[#e2e8f0] bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wide">From</label>
          <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)}
            className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wide">To</label>
          <input type="date" value={rangeEnd} min={rangeStart} onChange={(e) => setRangeEnd(e.target.value)}
            className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
        </div>
        <button onClick={handleLoad} disabled={!rangeStart || !rangeEnd || rangeStart > rangeEnd}
          className="rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] disabled:opacity-50 transition-colors">
          Load
        </button>
        {exceedsMax && <p className="text-xs text-[#d97706] self-center">Max 31 days</p>}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : allRecords.length === 0 ? (
        <EmptyState icon={<Clock className="h-7 w-7 text-[#94a3b8]" />}
          title="No records for this range"
          description="Try a different date range or check that attendance has been recorded." />
      ) : (
        <div className="space-y-4">
          <SummaryChips counts={rangeCounts} activeFilter={statusFilter} onFilter={setStatusFilter} />

          {/* Search + count */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2">
              <Search className="h-4 w-4 text-[#94a3b8] shrink-0" />
              <input type="text" value={nameSearch} onChange={(e) => setNameSearch(e.target.value)}
                placeholder="Search employee…"
                className="text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none w-40" />
            </div>
            <span className="text-xs text-[#64748b] ml-auto">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
          </div>

          <Card padding="none">
            <RecordsTable records={filtered} />
          </Card>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminAttendancePage() {
  const [viewMode, setViewMode] = useState<"single" | "range">("single");
  // Single-day: one state drives the query directly (no separate "loaded" state)
  const [date, setDate] = useState(todayIso());
  const [statusFilter, setStatusFilter] = useState<AttendanceStatus | "ALL">("ALL");
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const { toasts, dismissToast } = useToast();

  const { activeBusinessId } = useAuth();
  const bizId = activeBusinessId ?? "";
  const { isLoading, refetch, data } = useBizAttendance(bizId, date);
  const records: AttendanceRecord[] = data ?? [];

  const counts = useMemo(() => {
    const c = { PRESENT: 0, LATE: 0, ABSENT: 0, ON_LEAVE: 0 };
    for (const r of records) c[r.status]++;
    return c;
  }, [records]);

  const filteredRecords = useMemo(() =>
    statusFilter === "ALL" ? records : records.filter((r) => r.status === statusFilter),
    [records, statusFilter]
  );

  const editingRecord = editingUserId ? records.find((r) => r.userId === editingUserId) ?? null : null;

  if (!activeBusinessId) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-[#e2e8f0] bg-white py-24 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#eff3ff]">
          <Building2 className="h-8 w-8 text-[#2447d9]" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-[#0f172a]">No business selected</h2>
          <p className="mt-1 text-sm text-[#64748b]">Select a business from the top bar to manage attendance.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 shadow-sm space-y-4">
        {/* Title + mode toggle */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2447d9]">
              <Clock className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#0f172a]">Attendance</h1>
              <p className="text-xs text-[#64748b]">Record and review daily employee attendance</p>
            </div>
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-1">
            {(["single", "range"] as const).map((m) => (
              <button key={m} onClick={() => setViewMode(m)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  viewMode === m ? "bg-white text-[#0f172a] shadow-sm" : "text-[#64748b] hover:text-[#0f172a]"
                }`}>
                {m === "single" ? "Single Day" : "Date Range"}
              </button>
            ))}
          </div>
        </div>

        {/* Single-day controls — only shown in single mode */}
        {viewMode === "single" && (
          <div className="flex items-center gap-2 flex-wrap">
            {/* Prev / date picker / Next */}
            <button onClick={() => setDate(shiftDate(date, -1))}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#64748b] hover:bg-[#f1f5f9] transition-colors">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-1.5 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
            <button onClick={() => setDate(shiftDate(date, 1))} disabled={date >= todayIso()}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#64748b] hover:bg-[#f1f5f9] disabled:opacity-40 transition-colors">
              <ChevronRight className="h-4 w-4" />
            </button>
            {date !== todayIso() && (
              <button onClick={() => setDate(todayIso())}
                className="text-xs font-medium text-[#2447d9] hover:underline">
                Today
              </button>
            )}
            <span className="text-xs text-[#64748b] hidden sm:inline">{formatDisplayDate(date)}</span>

            {/* Spacer + actions */}
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={() => refetch()} title="Refresh"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#94a3b8] hover:text-[#64748b] hover:bg-[#f8fafc] transition-colors">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => setEditingUserId("__new__")}
                className="flex items-center gap-1.5 rounded-lg bg-[#2447d9] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors">
                <Plus className="h-4 w-4" />
                Mark Attendance
              </button>
            </div>
          </div>
        )}

        {/* Summary chips — only shown in single mode with data */}
        {viewMode === "single" && !isLoading && records.length > 0 && (
          <SummaryChips counts={counts} activeFilter={statusFilter} onFilter={setStatusFilter} />
        )}
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      {viewMode === "range" ? (
        <DateRangeView bizId={bizId} />
      ) : (
        <Card padding="none">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#e2e8f0]">
            <p className="text-sm font-semibold text-[#0f172a]">
              {formatDisplayDate(date)}
              {!isLoading && records.length > 0 && (
                <span className="ml-2 text-xs font-normal text-[#94a3b8]">{records.length} entries</span>
              )}
            </p>
            {!isLoading && statusFilter !== "ALL" && (
              <button onClick={() => setStatusFilter("ALL")} className="text-xs text-[#2447d9] hover:underline">
                Clear filter
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : records.length === 0 ? (
            <EmptyState
              icon={<Clock className="h-7 w-7 text-[#94a3b8]" />}
              title="No attendance records for this date"
              description="Use 'Mark Attendance' above to add records."
              className="py-10"
            />
          ) : (
            <RecordsTable records={filteredRecords} onEdit={setEditingUserId} />
          )}
        </Card>
      )}

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {editingUserId && (
        <Modal
          isOpen={!!editingUserId}
          onClose={() => setEditingUserId(null)}
          title={editingUserId === "__new__" ? "Mark Attendance" : "Edit Attendance"}
          size="sm"
        >
          {editingUserId === "__new__" ? (
            <NewAttendanceEntry date={date} onClose={() => setEditingUserId(null)}
              onSaved={() => { setEditingUserId(null); refetch(); }} />
          ) : editingRecord ? (
            <EditAttendanceModal record={editingRecord} date={date}
              onClose={() => setEditingUserId(null)} onSaved={() => refetch()} />
          ) : null}
        </Modal>
      )}
    </div>
  );
}
